import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCampaignEmailHub } from './CampaignEmailHubContext';
import { useCampaignWorkspace } from '../CampaignWorkspaceContext';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { updateCampaign } from '../../../api/campaigns';
import {
  fetchHubupGroupMessages,
  fetchHubupGroupTopics,
  fetchMyHubupCommunities,
} from '../../../api/hubup';
import { useUserAccess } from '../../../context/UserAccessContext';
import EmailButtonDesignFields from '../../../components/campaigns/EmailButtonDesignFields';
import {
  hubupButtonDesignFromSource,
  hubupButtonDesignToEmailFields,
} from '../../../utils/emailButtonStyles';

const fieldClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

export default function CampaignEmailHubupRoute() {
  const workspace = useCampaignWorkspace();
  const hub = useCampaignEmailHub();
  const campaign = workspace.campaign ?? hub.campaign;
  const campaignId = workspace.campaignId || hub.campaignId;
  const reloadCampaign = workspace.reloadCampaign ?? hub.reloadCampaign;
  const canManageCampaigns = workspace.canManageCampaigns === true;
  const { user, chooseAppMode } = useUserAccess();
  const navigate = useNavigate();
  const canUseHubup = user?.permission === 'admin' || user?.hubup === true;

  const [communities, setCommunities] = useState([]);
  const [loadingCommunities, setLoadingCommunities] = useState(false);
  const [groupId, setGroupId] = useState('');
  const [saving, setSaving] = useState(false);
  const [hint, setHint] = useState('');
  const [topics, setTopics] = useState([]);
  const [history, setHistory] = useState([]);
  const [hubupButtonDesign, setHubupButtonDesign] = useState(() =>
    hubupButtonDesignFromSource(campaign),
  );

  useEffect(() => {
    setGroupId(campaign?.hubupGroupId || '');
    setHubupButtonDesign(hubupButtonDesignFromSource(campaign));
  }, [campaign]);

  useEffect(() => {
    if (!canUseHubup) return;
    let cancelled = false;
    (async () => {
      setLoadingCommunities(true);
      try {
        const res = await fetchMyHubupCommunities();
        if (!cancelled) setCommunities(res?.data?.communities || []);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoadingCommunities(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canUseHubup]);

  useEffect(() => {
    if (!groupId) {
      setTopics([]);
      setHistory([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [tRes, mRes] = await Promise.all([
          fetchHubupGroupTopics(groupId),
          fetchHubupGroupMessages(groupId),
        ]);
        if (cancelled) return;
        setTopics(tRes?.data?.topics || []);
        setHistory(mRes?.data?.messages || []);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  const selectedCommunity = useMemo(
    () => communities.find((c) => c.id === groupId) || null,
    [communities, groupId],
  );

  const saveHubup = async () => {
    if (!campaignId) return;
    setSaving(true);
    setHint('');
    try {
      await updateCampaign(campaignId, {
        hubupGroupId: groupId || null,
        hubupGroupName: groupId ? selectedCommunity?.name || null : null,
        ...hubupButtonDesignToEmailFields(hubupButtonDesign),
      });
      await reloadCampaign?.();
      setHint('Configuration HubUP enregistrée.');
      setTimeout(() => setHint(''), 4000);
    } catch (e) {
      setHint(e?.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const openHubup = (path) => {
    chooseAppMode?.('hubup');
    navigate(path);
  };

  if (!canUseHubup) {
    return (
      <div className="rounded-xl border border-border bg-muted/20 p-6 text-sm text-muted-foreground">
        HubUP n&apos;est pas activé sur votre compte. Les variables HubUP dans les mails ne
        pourront pas être liées à une communauté.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="space-y-4">
        <section className="rounded-xl border border-border bg-card p-5 shadow-sm">
          <h2 className="text-base font-semibold text-foreground">Communauté liée</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Choisissez la communauté HubUP utilisée pour les notifications et les boutons dans
            vos emails de campagne.
          </p>
          {loadingCommunities ? (
            <p className="mt-4 text-sm text-muted-foreground">Chargement…</p>
          ) : communities.length === 0 ? (
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
              <p className="text-sm text-muted-foreground">Aucune communauté disponible.</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => openHubup('/hubup')}
              >
                Ouvrir HubUP
              </Button>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              <select
                className={fieldClass}
                value={groupId}
                disabled={!canManageCampaigns}
                onChange={(e) => setGroupId(e.target.value)}
              >
                <option value="">Aucune communauté</option>
                {communities.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {groupId ? (
                <p className="text-xs text-muted-foreground">
                  {topics.length} rubrique(s) · historique campagne ci-dessous
                </p>
              ) : null}
            </div>
          )}
        </section>

        <section className="rounded-xl border border-border bg-card p-5 shadow-sm">
          <EmailButtonDesignFields
            title="Design du bouton HubUP (campagne)"
            titleTooltip="Style par défaut pour %hubup_notification_button% dans vos templates."
            value={hubupButtonDesign}
            onChange={canManageCampaigns ? setHubupButtonDesign : () => {}}
          />
        </section>

        <div className="flex flex-wrap gap-2">
          {canManageCampaigns ? (
          <Button
            type="button"
            iconName="Save"
            iconPosition="left"
            onClick={saveHubup}
            disabled={saving}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
          ) : null}
          {groupId ? (
            <Button
              type="button"
              variant="outline"
              iconName="Radio"
              iconPosition="left"
              onClick={() => openHubup(`/hubup/g/${groupId}`)}
            >
              Publier une notification
            </Button>
          ) : null}
        </div>
        {hint ? <p className="text-sm text-muted-foreground">{hint}</p> : null}
      </div>

      <section className="rounded-xl border border-border bg-card p-5 shadow-sm">
        <h2 className="text-base font-semibold text-foreground">Historique campagne</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Notifications enregistrées sur cette campagne (utilisables à l&apos;envoi).
        </p>
        <ul className="mt-4 space-y-2">
          {(campaign?.hubupNotificationHistory || []).length === 0 ? (
            <li className="text-sm text-muted-foreground">Aucune notification pour l&apos;instant.</li>
          ) : (
            campaign.hubupNotificationHistory.map((item, i) => (
              <li
                key={item.messageId || i}
                className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-sm"
              >
                <span className="font-medium text-foreground">{item.title || 'Sans titre'}</span>
                {item.topicTitle ? (
                  <span className="ml-2 text-xs text-muted-foreground">{item.topicTitle}</span>
                ) : null}
              </li>
            ))
          )}
        </ul>
      </section>
    </div>
  );
}
